export interface RequestQueueConfig {
  key: string
  params?: unknown
  promise: (params?: unknown) => Promise<unknown>
  patchPromise?: (params?: unknown) => Promise<unknown>
  finale?: (params?: unknown) => Promise<unknown>
}

interface RequestQueueItem extends RequestQueueConfig {
  resolve: (value?: unknown) => void
  reject: (reason?: unknown) => void
}

export default class RequestQueue {
  private queue: RequestQueueItem[] = []
  private isProcessing = false

  // Method to add a request to the queue
  enqueue(request: RequestQueueConfig): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.queue.push({
        ...request,
        resolve,
        reject
      })
      this.processQueue()
    })
  }

  // Method to process the queue
  private async processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return
    }

    if (this.queue.length === 1 || !this.queue[0]?.patchPromise) {
      await this.processSingleRequest()
    } else {
      await this.processMultipleRequests()
    }

    this.isProcessing = false
    this.processQueue() // Process the queue again
  }

  private async processMultipleRequests() {
    const patchPromise = this.queue[0]?.patchPromise
    const finale = this.queue[0]?.finale

    if (patchPromise) {
      this.isProcessing = true

      const patchParams: unknown[] = []
      const requests: RequestQueueItem[] = []
      while (this.queue.length > 0) {
        const requestConfig = this.queue.shift() // Remove request config from the queue one by one
        if (!requestConfig) continue
        patchParams.push(requestConfig.params)
        requests.push(requestConfig)
      }

      try {
        const response = await patchPromise(patchParams) // Execute the patch request
        if (finale) {
          await finale() // Execute the finale request
        }
        requests.forEach(request => request.resolve(response))
      } catch (error) {
        console.error("Request queue error:", error)
        requests.forEach(request => request.reject(error))
      }
    }
  }

  private async processSingleRequest() {
    const requestConfig = this.queue.shift() // Get the first request from the queue
    if (requestConfig) {
      this.isProcessing = true

      try {
        const response = await requestConfig.promise(requestConfig.params) // Execute the request
        if (requestConfig.finale) {
          await requestConfig.finale() // Execute the finale request
        }
        requestConfig.resolve(response)
      } catch (error) {
        console.error("Request queue error:", error)
        requestConfig.reject(error)
      }
    }
  }
}
